/**
 * BusinessIntroVideo
 *
 * A reusable "watch this partner's video intro" widget with two visual
 * variants and a shared lazy-loaded YouTube modal.
 *
 *   variant="thumbnail"  → 16:9 YouTube thumbnail with play overlay.
 *                          Used on the BusinessProfilePage hero.
 *
 *   variant="pill"       → compact pill/chip.
 *                          Used inline on shortlist cards, saved matches, etc.
 *
 * The iframe is only mounted after the user clicks (lazy embed) so we never
 * hurt LCP or ship YouTube's tracking JS on page load. We use
 * youtube-nocookie.com as an extra privacy hardening.
 *
 * Silently renders nothing if the URL is missing or unparseable, so callers
 * can safely mount it unconditionally.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { PlayCircle, Play, X } from 'lucide-react';
import VideoGlow from '../media/VideoGlow';

/**
 * Extract an 11-character YouTube video ID from any of the common URL shapes:
 *   https://www.youtube.com/watch?v=XXXXXXXXXXX
 *   https://youtu.be/XXXXXXXXXXX
 *   https://www.youtube.com/embed/XXXXXXXXXXX
 *   https://www.youtube.com/shorts/XXXXXXXXXXX
 *   https://m.youtube.com/watch?v=XXXXXXXXXXX
 * Returns null when nothing usable is found.
 */
export function extractYouTubeId(url) {
  if (!url || typeof url !== 'string') return null;
  const patterns = [
    /[?&]v=([A-Za-z0-9_-]{11})/,       // watch?v=
    /youtu\.be\/([A-Za-z0-9_-]{11})/,   // youtu.be/
    /\/embed\/([A-Za-z0-9_-]{11})/,     // /embed/
    /\/shorts\/([A-Za-z0-9_-]{11})/,    // /shorts/
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m && m[1]) return m[1];
  }
  return null;
}

/**
 * Detect a direct-hosted video file (mp4/webm/mov). These are served
 * from our own static origin (e.g. /videos/lamina-stone-intro.mp4) and
 * rendered with a native <video> element rather than a YouTube iframe.
 * Anything else (Vimeo, Google Drive, Wistia) is not handled here yet.
 */
export function isDirectVideoUrl(url) {
  if (!url || typeof url !== 'string') return false;
  return /\.(mp4|webm|mov|m4v)(\?|#|$)/i.test(url);
}

/**
 * Lock body scroll while the modal is open.
 */
function useBodyScrollLock(active) {
  useEffect(() => {
    if (!active) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [active]);
}

function VideoModal({ videoId, videoUrl, businessName, onClose }) {
  useBodyScrollLock(true);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const embedSrc = `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1&playsinline=1`;
  const youtubeUrl = videoUrl || `https://www.youtube.com/watch?v=${videoId}`;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Video intro from ${businessName || 'this partner'}`}
      onClick={onClose}
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 sm:p-8"
      data-testid="business-intro-video-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-[960px]"
      >
        <div className="relative aspect-video bg-black rounded-2xl overflow-hidden shadow-2xl">
          <iframe
            src={embedSrc}
            title={`Video intro from ${businessName || 'this partner'}`}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            className="absolute inset-0 w-full h-full"
          />
          <button
            type="button"
            onClick={onClose}
            className="absolute top-3 right-3 w-10 h-10 rounded-full bg-black/70 hover:bg-black/90 text-white flex items-center justify-center transition-colors"
            aria-label="Close video"
            data-testid="business-intro-video-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        {/* Fallback link: some brand channels disable embedded playback. This
            gives the user a one-click path to the video on YouTube itself. */}
        <div className="mt-3 flex justify-center">
          <a
            href={youtubeUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-white/70 hover:text-white text-[12px] font-medium transition-colors"
            data-testid="business-intro-video-youtube-link"
          >
            Not loading? Watch on YouTube
            <span aria-hidden="true">↗</span>
          </a>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * DirectVideoModal - native <video> playback for MP4/WebM URLs. Used
 * when a partner supplies a self-hosted intro instead of a YouTube link.
 * The <video> element gets its own controls; no fallback link needed
 * because the file is served from our origin and always plays.
 */
function DirectVideoModal({ videoUrl, businessName, onClose }) {
  useBodyScrollLock(true);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Video intro from ${businessName || 'this partner'}`}
      onClick={onClose}
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 sm:p-8"
      data-testid="business-intro-video-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-[960px]"
      >
        <div className="relative aspect-video bg-black rounded-2xl overflow-hidden shadow-2xl">
          <video
            src={videoUrl}
            controls
            autoPlay
            playsInline
            preload="auto"
            className="absolute inset-0 w-full h-full bg-black"
            data-testid="business-intro-video-native"
          >
            Sorry, your browser doesn&apos;t support embedded videos.
          </video>
          <button
            type="button"
            onClick={onClose}
            className="absolute top-3 right-3 w-10 h-10 rounded-full bg-black/70 hover:bg-black/90 text-white flex items-center justify-center transition-colors"
            aria-label="Close video"
            data-testid="business-intro-video-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Thumbnail variant: 16:9 clickable YouTube thumbnail with play overlay.
 * Fits the profile-page hero.
 */
function ThumbnailButton({ videoId, businessName, onOpen }) {
  // YouTube's hqdefault is 480x360 - free, no key needed, no CORS.
  // Falls back automatically to the "no thumbnail" default if unavailable.
  const thumbUrl = `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;

  return (
    <VideoGlow poster={thumbUrl} className="rounded-xl">
    <button
      type="button"
      onClick={onOpen}
      className="group relative block w-full aspect-video rounded-xl overflow-hidden bg-black/40 border border-white/10 hover:border-white/30 transition-all shadow-lg"
      aria-label={`Watch video intro from ${businessName || 'this partner'}`}
      data-testid="business-intro-video-thumbnail"
    >
      <img
        src={thumbUrl}
        alt=""
        loading="lazy"
        decoding="async"
        className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
      />
      {/* Play overlay */}
      <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-t from-black/60 via-black/10 to-transparent">
        <div className="flex items-center justify-center w-16 h-16 rounded-full bg-white/95 group-hover:bg-white shadow-xl group-hover:scale-105 transition-transform">
          <Play className="w-6 h-6 text-[#1d1d1f] ml-1" fill="currentColor" />
        </div>
      </div>
      {/* Label chip */}
      <div className="absolute bottom-2 left-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/70 text-white text-[11px] font-medium tracking-wide">
        <PlayCircle className="w-3 h-3" />
        <span>Watch intro</span>
      </div>
    </button>
    </VideoGlow>
  );
}

/**
 * DirectThumbnailButton - same visual language as ThumbnailButton, but
 * uses a native <video preload="metadata"> to render the first frame as
 * the poster. No external image request, no reliance on a hosted
 * thumbnail. Muted + no controls so it stays a still until clicked.
 *
 * 2026-02-28: `posterUrl` prop added. When supplied, we render a plain
 * <img> instead of the <video> element for the thumbnail state - avoids
 * loading video bytes on page mount and lets us pick a brighter frame
 * than the video's actual first frame (which is often black).
 */
function DirectThumbnailButton({ videoUrl, posterUrl, businessName, onOpen }) {
  return (
    <VideoGlow poster={posterUrl} className="rounded-xl">
    <button
      type="button"
      onClick={onOpen}
      className="group relative block w-full aspect-video rounded-xl overflow-hidden bg-black border border-white/10 hover:border-white/30 transition-all shadow-lg"
      aria-label={`Watch video intro from ${businessName || 'this partner'}`}
      data-testid="business-intro-video-thumbnail"
    >
      {posterUrl ? (
        <img
          src={posterUrl}
          alt=""
          loading="lazy"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover opacity-95 group-hover:opacity-100 transition-opacity"
        />
      ) : (
        <video
          src={`${videoUrl}#t=0.5`}
          muted
          playsInline
          preload="metadata"
          aria-hidden="true"
          tabIndex={-1}
          className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity pointer-events-none"
        />
      )}
      <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-t from-black/60 via-black/10 to-transparent">
        <div className="flex items-center justify-center w-16 h-16 rounded-full bg-white/95 group-hover:bg-white shadow-xl group-hover:scale-105 transition-transform">
          <Play className="w-6 h-6 text-[#1d1d1f] ml-1" fill="currentColor" />
        </div>
      </div>
      <div className="absolute bottom-2 left-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/70 text-white text-[11px] font-medium tracking-wide">
        <PlayCircle className="w-3 h-3" />
        <span>Watch intro</span>
      </div>
    </button>
    </VideoGlow>
  );
}

/**
 * Pill variant: compact, inline chip. Used on shortlist/matches cards.
 */
function PillButton({ businessName, onOpen, size = 'sm' }) {
  const sizing = size === 'xs'
    ? 'text-[10px] px-2 py-[3px] gap-1'
    : 'text-[11px] px-2.5 py-1 gap-1.5';
  return (
    <button
      type="button"
      onClick={onOpen}
      className={`inline-flex items-center rounded-full bg-[#1d8efe] text-white font-medium hover:bg-[#0f7ae5] active:bg-[#0a6bcf] transition-colors ${sizing}`}
      aria-label={`Watch video intro from ${businessName || 'this partner'}`}
      data-testid="business-intro-video-pill"
    >
      <PlayCircle className={size === 'xs' ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
      <span>Watch intro</span>
    </button>
  );
}

export default function BusinessIntroVideo({
  url,
  posterUrl,
  businessName,
  variant = 'pill',
  size = 'sm',
}) {
  const [open, setOpen] = useState(false);
  const videoId = extractYouTubeId(url);
  const directVideo = !videoId && isDirectVideoUrl(url);
  const handleOpen = useCallback((e) => {
    // Prevent accidental navigation when the button is nested inside a link/card.
    if (e && e.stopPropagation) e.stopPropagation();
    if (e && e.preventDefault) e.preventDefault();
    setOpen(true);
  }, []);
  const handleClose = useCallback(() => setOpen(false), []);

  // Nothing renderable - not a YouTube link, not a supported direct video.
  if (!videoId && !directVideo) return null;

  return (
    <>
      {variant === 'thumbnail' ? (
        directVideo ? (
          <DirectThumbnailButton videoUrl={url} posterUrl={posterUrl} businessName={businessName} onOpen={handleOpen} />
        ) : (
          <ThumbnailButton videoId={videoId} businessName={businessName} onOpen={handleOpen} />
        )
      ) : (
        <PillButton businessName={businessName} onOpen={handleOpen} size={size} />
      )}
      {open && (
        directVideo ? (
          <DirectVideoModal
            videoUrl={url}
            businessName={businessName}
            onClose={handleClose}
          />
        ) : (
          <VideoModal
            videoId={videoId}
            videoUrl={url}
            businessName={businessName}
            onClose={handleClose}
          />
        )
      )}
    </>
  );
}
